import { LockClosedIcon } from "@heroicons/react/24/outline";
import { ArrowDownIcon, StarIcon } from "@heroicons/react/24/solid";
import React from "react";
import { formatNumberWithSeparator, isExpired } from "../../Hooks/UseGenericFunctions";
import { AddToBasketButton } from "../../Components/ShoppingBasket/AddToBasketButton";
import { DivCell } from "./DivCell";
import { PurchaseTypePill } from "../../Components/Information/PurchaseTypePill";
import { OfferInput } from "./OfferInput";
import { VATIcon } from "../../Components/Cars/VATIcon";
import { CarzelleTooltip } from "../../Components/System/Messages/CarzelleTooltip";
import { PartnerListingCountDown } from "./PartnerListingCountDown";
import { EquipmentIcons } from "../../Components/Equipment/EquipmentIcons";
import { LikeCar } from "./LikeCar";
import { isColumnVisible } from "./PresalesTableConfig";

export const PresalesListTableRow = React.memo(({ car, hidePrices, equipmentIcons, activeCarId, fnUpdateCarList, fnSetActiveCar, columns }) => {
    function formatNumber(number = 'en-US') {
        return new Intl.NumberFormat('da-DK').format(Math.floor(number));
    }
    
    const handleRowClick = () => {
        fnSetActiveCar(car);
    }

    // Column rendering functions
    const renderColumn = (column) => {
        const cellClassName = `${column.width} ${column.responsiveClass || ''}`;
        const conditionalClassName = column.conditionalVisibility === 'hidePrices' && hidePrices ? 'hidden' : '';
        const finalClassName = `${cellClassName} ${conditionalClassName}`.trim();

        switch (column.key) {
            case 'like':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <LikeCar car={car} fnAfterUpdate={fnUpdateCarList} />
                    </DivCell>
                );

            case 'image':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <div className="h-full w-[36px] relative">
                            <img id={"car_main_image_" + car.hash} className=' h-full my-1 object-scale-down rounded-full' src={car.list_image} alt={'image for ' + car.designation} loading="lazy" />
                            {car.new_on_list === 'true' &&
                                <div className="absolute left-[-10px] top-[-0px]">
                                    <CarzelleTooltip message="This car is new on the list">
                                        <div className="rounded-full p-1 bg-green-600 text-white"><StarIcon className="w-3 h-3" /></div>
                                    </CarzelleTooltip>
                                </div>
                            }
                        </div>
                    </DivCell>
                );

            case 'carId':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <div className="flex items-center">
                            #{car.car_id}
                        </div>
                    </DivCell>
                );

            case 'description':
                return (
                    <DivCell key={column.key} className={`${finalClassName} max-w-[120px] 2xl:max-w-[250px] relative text-nowrap overflow-hidden text-ellipsis pt-[2px] h-[22px]`}>
                        <div className="mr-2">
                            <CarzelleTooltip message={car.designation}>
                                <div>{car.designation}</div>
                            </CarzelleTooltip>
                        </div>
                        {car.expires_at_rest !== "" &&
                            <div className="absolute right-0 top-[2px]">
                                <PartnerListingCountDown seconds={car.expires_at_rest} />
                            </div>
                        }
                    </DivCell>
                );

            case 'status':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <PurchaseTypePill car={car} />
                    </DivCell>
                );

            case 'location':
                return (
                    <DivCell key={column.key} className={`${finalClassName} overflow-hidden whitespace-nowrap`}>
                        {car.stock_location}
                    </DivCell>
                );

            case 'firstReg':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <span className="overflow-hidden overflow-ellipsis whitespace-nowrap text-right pr-4">
                            {car.reg_date_show}
                        </span>
                    </DivCell>
                );

            case 'km':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <span className=" overflow-hidden overflow-ellipsis whitespace-nowrap flex justify-end pr-4">
                            {formatNumber(car.km)}
                        </span>
                    </DivCell>
                );

            case 'damages':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        {(Number(car.damage_amount) >= 0) &&
                            <span className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-4 flex items-center justify-end">
                                <div>{formatNumberWithSeparator(Number(car.damage_amount), '.')}€</div>
                            </span>
                        }
                    </DivCell>
                );

            case 'price':
                return (
                    <DivCell key={column.key} className={`p-0 ${finalClassName}`}>
                        <span className="overflow-hidden overflow-ellipsis whitespace-nowrap p-0">
                            <div className="flex items-center relative">
                                {car.price_info !== '' && !car.allow_offers &&
                                    <div className="absolute left-0">
                                        <CarzelleTooltip message={car.price_info}>
                                            <div className="rounded-full p-1 bg-green-600 text-white"><ArrowDownIcon className="w-3 h-3" /></div>
                                        </CarzelleTooltip>
                                    </div>
                                }
                                <div className="min-w-[100px] text-right">{formatNumber(car.price)}&nbsp;{car.currency}</div>
                                <div className='flex items-center ml-2'><VATIcon vatStatusId={car.vat_status_id} /></div>
                            </div>
                        </span>
                    </DivCell>
                );

            case 'buy':
                return (
                    <DivCell key={column.key} className={`flex justify-start ${car.counter_offer === 1 ? 'w-[200px]' : finalClassName}`}>
                        {!isExpired(car.expires_at_rest) &&
                            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap">
                                <AddToBasketButton car={car} fnUpdate={fnUpdateCarList} bgColor={`${car.allow_offers || car.buy_status !== 'listing' ? 'bg-purchase-color' : 'bg-presales'} `} carInBasket={car.in_basket} />
                            </div>
                        }
                    </DivCell>
                );

            case 'offer':
                return (
                    <DivCell key={column.key} className={`${car.counter_offer === 1 ? 'hidden' : ''} ${finalClassName}`}>
                        {!isExpired(car.expires_at_rest) && !car.fixed_price &&
                            <div onClick={(e) => e.stopPropagation()}>
                                <OfferInput car={car} fnUpdate={fnUpdateCarList} className="w-[110px] h-[30px] text-[12px] leading-[12px]" />
                            </div>
                        }
                        {car.fixed_price &&
                            <div className="flex items-center">
                                <div><LockClosedIcon className="w-4 h-4 text-gray-400 mr-2" /></div>
                                <div className="text-xs">Fixed price</div>
                            </div>
                        }
                    </DivCell>
                );

            case 'equipment':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <EquipmentIcons carId={car.car_id} equipment={car.equipment_base_icons} equipmentBaseIcons={equipmentIcons} iconIdPrefix="presales" size="16px" opacity={0.5} />
                    </DivCell>
                );

            case 'horsepower':
                return (
                    <DivCell key={column.key} className={`${finalClassName} text-right`}>
                        {car.horsepower}
                    </DivCell>
                );

            case 'color':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <span className="block overflow-hidden overflow-ellipsis whitespace-nowrap">
                            <div>{car.color}</div>
                        </span>
                    </DivCell>
                );

            case 'fuel':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <span className="block overflow-hidden overflow-ellipsis whitespace-nowrap">
                            <div>{car.fuel_name}</div>
                        </span>
                    </DivCell>
                );

            case 'gear':
                return (
                    <DivCell key={column.key} className={finalClassName}>
                        <span className="block overflow-hidden overflow-ellipsis whitespace-nowrap">
                            <div>{car.gear_name}</div>
                        </span>
                    </DivCell>
                );

            default:
                return null;
        }
    };

    return (
        <div key={car.hash} onClick={handleRowClick} className={`flex items-center border-l-[5px] ${car.in_basket !== null ? 'border-purchase-color bg-gray-100 ' : activeCarId === car.hash ? 'border-l-gray-200 bg-gray-200' : 'border-l-white bg-white'} px-2 h-[41px] text-[12px] text-gray-500 cursor-pointer hover:bg-gray-100 hover:border-l-gray-100 hover:shadow transition duration-150 ease-in-out border-gray-100`}>
            {columns.map((column) => {
                if (!isColumnVisible(column, hidePrices)) return null;
                return renderColumn(column);
            })}
        </div>
    )
});
